import { Navigate } from 'react-router-dom'
import type { ReactNode } from 'react'
import { useAuth } from '../lib/auth'
import { useRole, type ModuleKey } from '../lib/useRole'

type RoleGuardProps = {
  /** Módulo funcional a proteger. Se resuelve con la matriz `MODULE_ACCESS`. */
  module: ModuleKey
  children: ReactNode
  /** A dónde redirigir si el user no tiene permiso. Default `/neocore`. */
  fallback?: string
}

/**
 * Route-level guard: verifica que el user actual tenga permiso para el
 * `module` indicado. Si no, muestra pantalla de "sin permisos" con opción
 * de volver al home. No hace redirect duro para no reciclar loops en caso
 * de sesión medio cargada.
 */
export function RoleGuard({ module, children, fallback = '/neocore' }: RoleGuardProps) {
  const { session, loading } = useAuth()
  const { canAccess, role } = useRole()

  if (loading) {
    return (
      <div style={{ padding: 32, opacity: 0.6, fontSize: 14 }}>
        Cargando sesión…
      </div>
    )
  }

  // Sin sesión → al login (lo maneja el router global también, pero por si acaso)
  if (!session) {
    return <Navigate to="/login" replace />
  }

  if (!canAccess(module)) {
    return (
      <div style={{ padding: '48px 24px', maxWidth: 480, margin: '0 auto', textAlign: 'center' }}>
        <div style={{ fontSize: 40, marginBottom: 12 }}>🔒</div>
        <h2 style={{ margin: '0 0 8px', fontSize: 20 }}>Sin permisos</h2>
        <p style={{ margin: '0 0 20px', color: '#6b7280', fontSize: 14, lineHeight: 1.5 }}>
          Tu rol actual (<strong>{role ?? 'sin rol'}</strong>) no tiene acceso a este módulo.
          Si creés que es un error, pedile al admin de Neocoop que ajuste tus permisos.
        </p>
        <a
          href={fallback}
          style={{
            display: 'inline-block',
            padding: '10px 18px',
            background: '#0f172a',
            color: 'white',
            borderRadius: 8,
            textDecoration: 'none',
            fontSize: 14,
            fontWeight: 600,
          }}
        >
          Volver al inicio
        </a>
      </div>
    )
  }

  return <>{children}</>
}
